import { useI18n } from '#imports'
import { getLanguageCode } from '@/utils/languageCode'

/**
 * CDN 資源管理 Composable
 * 開發環境使用本地資源，正式環境使用 CDN
 */

export const useCdnAssets = () => {
  const { intranet, cdn, clientPoolId, clientId, gitSha } = useRuntimeConfig().public
  const { locale } = useI18n()

  /**
   * 取得後台上傳圖片資源的完整 CDN URL 如：活動圖、公告圖
   * 無論任何環境都回傳完整的 CDN 路徑
   * @param {string} path - 資源相對路徑 (例如: 'uploads/avatar.png')
   * @returns {string} 完整的 CDN URL
   */
  const getCdnUrl = (path) => {
    if (!path) return false
    try {
      const { pathname, search, hash } = new URL(path)
      return `${cdn}${pathname}${search}${hash}`
    } catch {
      return path
    }
  }

  /**
   * 取得資源的完整 URL
   * 開發環境: 使用本地路徑
   * 正式環境: 使用 CDN URL
   * 支援 {locale} 佔位符，自動替換為當前語系代碼
   * @param {string} path - 資源相對路徑 (例如: 'logo.png' 或 'images/login/login_btn_{locale}_normal.png')
   * @returns {string} 完整的資源 URL
   * @example
   * getFrontCdnUrl('logo.png')
   * getFrontCdnUrl('images/login/login_btn_{locale}_normal.png') // {locale} 會被替換為當前語系代碼
   */
  const getFrontCdnUrl = (path) => {
    // 處理 {locale} 佔位符
    const resolvedPath = path.includes('{locale}') ? path.replace('{locale}', getLanguageCode(locale.value)) : path

    if (process.env.NODE_ENV === 'production' && intranet === '1') {
      return `${cdn}/site_resources/${clientPoolId}/pool/${gitSha}/${resolvedPath}`
    } else {
      return `/${resolvedPath}`
    }
  }

  /**
   * 取得後端資源的完整 CDN URL
   * 無論任何環境都回傳完整的 CDN 路徑
   * @param {string} path - 資源相對路徑 (例如: 'uploads/avatar.png')
   * @returns {string} 完整的 CDN URL
   */
  const getBackCdnUrl = (path) => {
    return `${cdn}/images/${clientId}/${path}`
  }

  /**
   * 取得後端音效的完整 CDN URL
   * 無論任何環境都回傳完整的 CDN 路徑
   * @param {string} path - 音效相對路徑 (例如: 'background/pwa_lobby.mp3')
   * @returns {string} 完整的 CDN URL
   */
  const getBackCdnSoundUrl = (path) => {
    return `${cdn}/sounds/${clientId}/${path}`
  }

  /**
   * 取得後端預設資源的完整 CDN URL
   * 無論任何環境都回傳完整的 CDN 路徑
   * @param {string} path - 資源相對路徑 (例如: 'games/game_default.png')
   * @returns {string} 完整的 CDN URL
   */
  const getBackCdnDefaultUrl = (path) => {
    return `${cdn}/images/default/${path}`
  }

  /**
   * 取得社群媒體圖示的 CDN URL
   * @param {string} iconType - 圖示類型 (例如: 'facebook', 'twitter')
   * @param {string} state - 圖示狀態 (預設: 'normal', 可選: 'hover', 'active')
   * @returns {string} 圖示的 CDN URL
   */
  const getSocialIconUrl = (iconType, state = 'normal') => {
    return getFrontCdnUrl(`images/affiliate/affiliate_btn_${iconType}_${state}.png`)
  }

  /**
   * 取得遊戲圖示的 CDN URL
   * @param {string} gameCode - 遊戲代碼
   * @returns {string} 遊戲圖示的 CDN URL
   */
  const getGameIconUrl = (gameCode) => {
    return getFrontCdnUrl(`game/${gameCode}.png`)
  }

  /**
   * 取得支付方式圖示的 CDN URL
   * @param {string} paymentMethod - 支付方式代碼
   * @returns {string} 支付圖示的 CDN URL
   */
  const getPaymentIconUrl = (paymentMethod) => {
    return getFrontCdnUrl(`payment/${paymentMethod}.png`)
  }

  return {
    getCdnUrl,
    getFrontCdnUrl,
    getBackCdnUrl,
    getBackCdnSoundUrl,
    getBackCdnDefaultUrl,
    getSocialIconUrl,
    getGameIconUrl,
    getPaymentIconUrl,
  }
}
